import type { ClientStorageItem } from '@/shared/api/clientStorage';

import { useMutation, useQuery } from '@tanstack/react-query';

import { API } from '@/shared/api';
import { queryClient } from '@/shared/config';

import { CLIENT_STORAGE_QUERY_KEYS } from '../lib/constants';

type UseClientStorageItemOptions = {
  enabled?: boolean;
};

type UseClientStorageItemResult = {
  data: ClientStorageItem | null;
  isDeleting: boolean;
  isError: boolean;
  isLoading: boolean;
  isNotFound: boolean;
  isSetting: boolean;
  deleteItem: () => void;
  setItem: (value: string) => void;
};

export const useClientStorageItem = (
  key: string,
  options: UseClientStorageItemOptions = {},
): UseClientStorageItemResult => {
  const { enabled = true } = options;

  const query = useQuery({
    enabled: enabled && Boolean(key),
    queryFn: async () => {
      try {
        return await API.getItem(key);
      } catch (error) {
        const apiError = error as { status?: number };
        if (apiError.status === 404) {
          return null;
        }
        throw error;
      }
    },
    queryKey: CLIENT_STORAGE_QUERY_KEYS.item(key),
    retry: (failureCount, error) => {
      const apiError = error as { status?: number };
      if (apiError.status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const setMutation = useMutation({
    mutationFn: (value: string) => API.setItem(key, value),
    mutationKey: CLIENT_STORAGE_QUERY_KEYS.setItem,
    onSuccess: (data: ClientStorageItem) => {
      queryClient.setQueryData(CLIENT_STORAGE_QUERY_KEYS.item(data.key), data);
      void queryClient.invalidateQueries({ queryKey: CLIENT_STORAGE_QUERY_KEYS.list });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => API.deleteItem(key),
    mutationKey: CLIENT_STORAGE_QUERY_KEYS.deleteItem,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: CLIENT_STORAGE_QUERY_KEYS.item(key) });
      void queryClient.invalidateQueries({ queryKey: CLIENT_STORAGE_QUERY_KEYS.list });
    },
  });

  return {
    data: query.data ?? null,
    isDeleting: deleteMutation.isPending,
    isError: query.isError,
    isLoading: query.isLoading,
    isNotFound: query.data === null,
    isSetting: setMutation.isPending,
    deleteItem: deleteMutation.mutate,
    setItem: setMutation.mutate,
  };
};
